import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AddClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  brokerageId: string;
}

export const AddClientModal = ({ isOpen, onClose, brokerageId }: AddClientModalProps) => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [assignedBrokerId, setAssignedBrokerId] = useState<string>('no-assignment');
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch brokers for assignment
  const { data: brokers = [] } = useQuery({
    queryKey: ['brokers', brokerageId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('brokers')
        .select('*')
        .eq('brokerage_id', brokerageId);
      
      if (error) {
        console.error('Error fetching brokers:', error);
        throw error;
      }
      
      return data || [];
    },
    enabled: !!brokerageId && isOpen
  });

  const addClientMutation = useMutation({
    mutationFn: async () => {
      // First, create the client
      const { data: client, error: clientError } = await supabase
        .from('clients')
        .insert({
          full_name: fullName || null,
          email: email || null,
          phone: phone || null,
          brokerage_id: brokerageId
        })
        .select()
        .single();

      if (clientError) {
        console.error('Error adding client:', clientError);
        throw clientError;
      }

      // If a broker is assigned, create a conversation
      if (assignedBrokerId && assignedBrokerId !== 'no-assignment' && client) {
        const { error: conversationError } = await supabase
          .from('conversations')
          .insert({
            client_id: client.id,
            broker_id: assignedBrokerId,
            brokerage_id: brokerageId,
            active: true,
            status: 'greeting'
          });

        if (conversationError) {
          console.error('Error creating conversation:', conversationError);
          throw conversationError;
        }
      }

      return client;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-all-conversations'] });
      toast({
        title: 'Success',
        description: 'Client added successfully',
      });
      onClose();
      resetForm();
    },
    onError: (error) => {
      console.error('Failed to add client:', error);
      toast({
        title: 'Error',
        description: 'Failed to add client. Please try again.',
        variant: 'destructive',
      });
    }
  });

  const resetForm = () => {
    setFullName('');
    setEmail('');
    setPhone('');
    setAssignedBrokerId('no-assignment');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Phone number is required, and at least one other field should be filled
    if (phone.trim() && (fullName.trim() || email.trim())) {
      addClientMutation.mutate();
    }
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add New Client</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="clientFullName">Full Name</Label>
            <Input
              id="clientFullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Enter client's full name (optional)"
            />
          </div>
          
          <div>
            <Label htmlFor="clientEmail">Email</Label>
            <Input
              id="clientEmail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter client's email (optional)"
            />
          </div>
          
          <div>
            <Label htmlFor="clientPhone">Phone Number *</Label>
            <Input
              id="clientPhone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Enter client's phone number"
              required
            />
          </div>
          
          <div>
            <Label htmlFor="assignBroker">Assign to Broker (Optional)</Label>
            <Select value={assignedBrokerId} onValueChange={setAssignedBrokerId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a broker (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-assignment">No assignment</SelectItem>
                {brokers.map((broker) => (
                  <SelectItem key={broker.id} value={broker.id}>
                    {broker.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={addClientMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                !phone.trim() || (!fullName.trim() && !email.trim()) || 
                addClientMutation.isPending
              }
            >
              {addClientMutation.isPending ? 'Adding...' : 'Add Client'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};